// src/components/telemetry/TrainingCrew.jsx
// Allenamento con le auto: chi guida l'auto selezionata, il cambio pilota e lo
// storico dei turni di guida (chi, dalle, alle, giri, migliore), nella
// sessione live. Il report PDF si scarica dal riepilogo della sessione.
//   driver        auto della sessione ({ car, pilot, stints, mac, ... })
//   pilots        anagrafica Partecipanti (per il cambio pilota)
//   busyPilots    Map pilotId → nome dell'auto che sta guidando (altre auto)
//   onChangePilot (mac, pilotId) => Promise<{ ok, error }>   null = sola lettura
//   onFixStint    (mac, n, pilotId) => Promise<{ ok, error }>
//   live          sessione in corso (turno aperto = "in corso")
//   onOpenEdit    () => void, all'apertura della scelta del pilota (ricarica l'anagrafica)
import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '../icons';
import PickField from '../PickField';
import { pilotItem, stintPilotName, fmtClock, fmtMinutes, fmtLapShort } from '../../utils/crew';
import './TrainingCrew.css';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);

export default function TrainingCrew({ driver, pilots = [], busyPilots = null, onChangePilot = null, onFixStint = null, live = false, onOpenEdit = null }) {
  // pannello di scelta: { mode: 'change' } oppure { mode: 'fix', n }
  const [edit, setEdit] = useState(null);
  const [choice, setChoice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [now, setNow] = useState(Date.now());

  const mac = driver?.mac;
  // cambio di auto selezionata: si chiude il pannello aperto
  useEffect(() => { setEdit(null); setChoice(null); setError(''); setNotice(''); }, [mac]);
  // durata del turno in corso
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, [live]);
  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(''), 6000);
    return () => clearTimeout(t);
  }, [notice]);

  const stints = useMemo(() => (Array.isArray(driver?.stints) ? driver.stints : []), [driver]);
  const current = stints.length ? stints[stints.length - 1] : null;
  const car = driver?.car || null;
  const editing = edit?.mode === 'fix' ? stints.find(s => s.n === edit.n) : null;
  const excludeId = edit?.mode === 'change' ? current?.pilot?.id : editing?.pilot?.id;

  const items = useMemo(() => pilots.map(p => {
    const it = pilotItem(p);
    if (String(p.id) === String(excludeId)) return { ...it, disabled: true, note: edit?.mode === 'change' ? 'è già alla guida' : 'è il pilota di questo turno' };
    // chi guida un'altra auto non può salire su questa (salvo correggere un turno chiuso)
    const busy = busyPilots?.get(String(p.id));
    const lockBusy = edit?.mode === 'change' || (editing && editing === current);
    return busy && lockBusy ? { ...it, disabled: true, note: `sta guidando ${busy}` } : it;
  }), [pilots, busyPilots, excludeId, edit, editing, current]);

  if (!driver || !car) return null;

  const chosen = choice ? pilots.find(p => String(p.id) === String(choice)) : null;
  const chosenName = chosen ? `${chosen.name || ''} ${chosen.surname || ''}`.trim() : '';

  const open = (e) => { setEdit(e); setChoice(null); setError(''); setNotice(''); if (onOpenEdit) onOpenEdit(); };
  const close = () => { setEdit(null); setChoice(null); setError(''); };
  const confirm = async () => {
    if (!choice || saving) return;
    setSaving(true); setError('');
    try {
      const r = edit.mode === 'change' ? await onChangePilot(mac, choice) : await onFixStint(mac, edit.n, choice);
      if (!r?.ok) throw new Error(r?.error || 'Operazione non riuscita');
      setNotice(edit.mode === 'change'
        ? (r.corrected ? `Turno corretto: alla guida c'è ${chosenName}.` : `Cambio pilota registrato: da adesso guida ${chosenName}.`)
        : `Turno ${edit.n} assegnato a ${chosenName}.`);
      close();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const specs = [car.type, car.power, car.transmission].filter(Boolean).join(' · ');
  const pilotPhoto = current?.pilot?.photoUrl ? `${API_BASE}${current.pilot.photoUrl}` : null;
  const canEdit = !!onChangePilot && live;

  return (
    <section className="tc">
      <header className="tc-head">
        <div className="tc-car">
          {car.photoUrl
            ? <img className="tc-car__img" src={`${API_BASE}${car.photoUrl}`} alt="" />
            : <span className="tc-car__img tc-car__img--ph"><Icon.Car size={20} /></span>}
          <div className="tc-car__text">
            <span className="tc-kicker">Auto</span>
            <strong>{car.name}{car.number ? <em>#{String(car.number).replace(/^#/, '')}</em> : null}</strong>
            {specs ? <span>{specs}</span> : null}
          </div>
        </div>

        <div className={`tc-driver ${live ? 'is-live' : ''}`}>
          {pilotPhoto
            ? <img className="tc-driver__img" src={pilotPhoto} alt="" />
            : <span className="tc-driver__img tc-driver__img--ph"><Icon.Users size={20} /></span>}
          <div className="tc-driver__text">
            <span className="tc-kicker">{live ? 'Alla guida' : 'Ultimo pilota'}</span>
            <strong>{stintPilotName(current)}</strong>
            {current && <span>dalle {fmtClock(current.from)}{live ? ` · da ${fmtMinutes(current.from, now)}` : ''} · {current.laps || 0} {current.laps === 1 ? 'giro' : 'giri'}</span>}
          </div>
          {canEdit && (
            <button type="button" className="rs-btn rs-btn--lime tc-change" onClick={() => open({ mode: 'change' })} disabled={edit?.mode === 'change'}>
              <Icon.Swap size={16} /> Cambia pilota
            </button>
          )}
        </div>
      </header>

      {edit && (
        <div className="tc-edit">
          <div className="tc-edit__title">
            {edit.mode === 'change'
              ? <><Icon.Swap size={15} /> Chi sale in auto adesso?</>
              : <><Icon.Edit size={14} /> Correggi il pilota del turno {edit.n}</>}
          </div>
          <div className="tc-edit__row">
            <div className="tc-edit__field">
              <PickField
                items={items}
                value={choice}
                onChange={setChoice}
                placeholder="Cerca il pilota…"
                emptyText={pilots.length ? 'Nessun pilota trovato' : 'Nessun pilota in Partecipanti'}
                icon={Icon.Users}
                clearLabel="Scegli un altro pilota"
                autoFocus
              />
            </div>
            <button type="button" className="rs-btn rs-btn--primary" onClick={confirm} disabled={!choice || saving}>
              {saving ? <><span className="rs-spinner" /> Salvataggio…</> : <><Icon.Check size={15} /> {edit.mode === 'change' ? 'Conferma cambio' : 'Correggi turno'}</>}
            </button>
            <button type="button" className="rs-btn rs-btn--ghost" onClick={close} disabled={saving}>Annulla</button>
          </div>
          <p className="tc-edit__hint">
            {edit.mode === 'change'
              ? 'Il nuovo turno parte adesso: i giri iniziati da qui in poi sono del nuovo pilota. Se hai sbagliato, scegli subito il pilota giusto: un turno senza giri viene corretto, non duplicato.'
              : 'I giri del turno passano al pilota scelto.'}
          </p>
          {error && <div className="rs-alert rs-alert--danger"><Icon.Alert /><span>{error}</span></div>}
        </div>
      )}
      {notice && !edit && <div className="rs-alert rs-alert--success tc-notice"><Icon.Check /><span>{notice}</span></div>}

      <div className="tc-list">
        <div className="tc-list__head">
          <span className="tc-list__title">Storico cambi pilota <b>{stints.length}</b></span>
        </div>
        <div className="tc-table-wrap">
          <table className="tc-table">
            <thead>
              <tr>
                <th className="num">Turno</th>
                <th>Pilota</th>
                <th className="num tc-col-time">Dalle</th>
                <th className="num tc-col-time">Alle</th>
                <th className="num tc-col-dur">Durata</th>
                <th className="num">Giri</th>
                <th className="num">Migliore</th>
                <th aria-label="Azioni" />
              </tr>
            </thead>
            <tbody>
              {[...stints].reverse().map(s => {
                const running = live && s === current;
                const end = s.to ?? (running ? now : null);
                return (
                  <tr key={s.n} className={running ? 'is-open' : ''}>
                    <td className="num tc-mono tc-n">{s.n}</td>
                    <td className="tc-who">
                      <strong>{stintPilotName(s)}</strong>
                      {running && <span className="tc-live">alla guida</span>}
                      <span className="tc-when">{fmtClock(s.from)} – {running ? 'in corso' : fmtClock(s.to)}</span>
                    </td>
                    <td className="num tc-mono tc-col-time">{fmtClock(s.from)}</td>
                    <td className="num tc-mono tc-col-time">{running ? <span className="tc-dim">in corso</span> : fmtClock(s.to)}</td>
                    <td className="num tc-mono tc-col-dur">{fmtMinutes(s.from, end)}</td>
                    <td className="num tc-mono">{s.laps ?? 0}</td>
                    <td className="num tc-mono tc-best">{fmtLapShort(s.bestLapTime)}</td>
                    <td className="tc-actions">
                      {onFixStint && live && (
                        <button type="button" className="rs-btn rs-btn--ghost rs-btn--sm rs-btn--icon" onClick={() => open({ mode: 'fix', n: s.n })}
                          title="Correggi il pilota di questo turno" aria-label={`Correggi il pilota del turno ${s.n}`}>
                          <Icon.Edit size={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
